/*
 * Purpose: Mission / builder panel renderer — shared by legacy builder + /mission
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { BuilderWorkspace } from '@/components/BuilderWorkspace';
import { SecretManager } from '@/components/SecretManager';
import { WorkflowManager } from '@/components/WorkflowManager';
import { TemplateLibrary } from '@/components/TemplateLibrary';
import { LogsViewer } from '@/components/LogsViewer';
import { WorkflowStats } from '@/components/WorkflowStats';
import { WorkflowVersioning } from '@/components/WorkflowVersioning';
import { WorkflowScheduler } from '@/components/WorkflowScheduler';
import { WorkflowIntegration } from '@/components/WorkflowIntegration';
import { WorkflowAnalytics } from '@/components/WorkflowAnalytics';
import { ExecutionMonitor } from '@/components/ExecutionMonitor';
import { ErrorDebugger } from '@/components/ErrorDebugger';
import { ActivityLog } from '@/components/ActivityLog';
import { WorkflowSettings } from '@/components/WorkflowSettings';
import { ConnectorLibraryBrowser } from '@/components/ConnectorLibraryBrowser';
import { WorkflowCollaboration } from '@/components/WorkflowCollaboration';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { BuilderPanelId } from '@/lib/productActivities';

export function MissionPanelContent({
  panel,
  mapShortcutsActive = false,
}: {
  panel: BuilderPanelId;
  mapShortcutsActive?: boolean;
}) {
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);

  switch (panel) {
    case 'builder':
      return (
        <div className="flex flex-1 flex-col min-h-0 h-full w-full">
          <BuilderWorkspace shortcutsActive={mapShortcutsActive} />
        </div>
      );
    case 'templates':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <TemplateLibrary />
        </div>
      );
    case 'secrets':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <SecretManager />
        </div>
      );
    case 'flows':
      return (
        <div className="max-w-2xl mx-auto p-4">
          <WorkflowManager />
        </div>
      );
    case 'logs':
      return (
        <div className="max-w-2xl mx-auto p-4">
          <LogsViewer workflowId={currentWorkflow?.id} />
        </div>
      );
    case 'stats':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <WorkflowStats />
        </div>
      );
    case 'connections':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <ConnectorLibraryBrowser />
        </div>
      );
    case 'versions':
      return (
        <div className="max-w-2xl mx-auto p-4">
          <WorkflowVersioning />
        </div>
      );
    case 'scheduler':
      return (
        <div className="max-w-2xl mx-auto p-4">
          <WorkflowScheduler />
        </div>
      );
    case 'integrations':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <WorkflowIntegration />
        </div>
      );
    case 'analytics':
      return (
        <div className="max-w-5xl mx-auto p-4">
          <WorkflowAnalytics />
        </div>
      );
    case 'monitor':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <ExecutionMonitor />
        </div>
      );
    case 'debug':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <ErrorDebugger />
        </div>
      );
    case 'collaborate':
      return (
        <div className="max-w-4xl mx-auto p-4">
          <WorkflowCollaboration />
        </div>
      );
    case 'activity':
      return (
        <div className="max-w-2xl mx-auto p-4">
          <ActivityLog />
        </div>
      );
    case 'settings':
      return (
        <div className="max-w-2xl mx-auto p-4">
          <WorkflowSettings />
        </div>
      );
    default:
      return null;
  }
}

const OPERATE_PANELS: Record<string, BuilderPanelId> = {
  scheduler: 'scheduler',
  monitor: 'monitor',
  debug: 'debug',
};

const INSIGHTS_PANELS: Record<string, BuilderPanelId> = {
  analytics: 'analytics',
  logs: 'logs',
  activity: 'activity',
  stats: 'stats',
};

const SETTINGS_PANELS: Record<string, BuilderPanelId> = {
  flow: 'settings',
  versions: 'versions',
  share: 'collaborate',
};

export function missionPanelFromParams(
  section: string | undefined,
  panel: string | undefined
): BuilderPanelId {
  if (!section || section === 'map') return 'builder';
  if (section === 'operate' && panel && OPERATE_PANELS[panel]) return OPERATE_PANELS[panel];
  if (section === 'insights' && panel && INSIGHTS_PANELS[panel]) return INSIGHTS_PANELS[panel];
  if (section === 'settings' && panel && SETTINGS_PANELS[panel]) return SETTINGS_PANELS[panel];
  return 'builder';
}
